import { ApiResponseError, type DeckPage as DeckPageData } from './decks.ts';
import type { DeckHistory } from '../../shared/contracts/deck-api.ts';
import type { DeckScope } from '../../shared/deck.ts';
import { deriveUnlockProgress } from '../../shared/unlock-progression.ts';
export type {
  DeckHistory,
  DeckHistoryCard,
  DeckHistoryEvent,
  DeckHistorySection,
  DeckHistoryTopic,
  DeckHistoryUnit,
} from '../../shared/contracts/deck-api.ts';

export type UploadMessageId =
  | 'web.deckPage.uploadInvalidPdf'
  | 'web.deckPage.uploadPageMismatch'
  | 'web.deckPage.uploadSuccess'
  | 'web.deckPage.uploadTooLarge'
  | 'web.deckPage.uploadUnsupported'
  | 'web.deckPage.uploadFailed';

export type DeckUploadItem = {
  messageId?: UploadMessageId;
  name: string;
  status: 'uploading' | 'result';
};

export type MemberDeckPage = Extract<DeckPageData, { isMember: true }>;
export type DeckHierarchy = Pick<DeckHistory, 'sections' | 'topics' | 'units'>;

export function applyDeckUnlockProgress(history: DeckHistory): DeckHistory {
  if (
    history.cards.some(
      (card) => !history.topics.some((topic) => topic.id === card.topicId),
    )
  )
    return history;
  const progression = deriveUnlockProgress({
    sections: history.sections,
    units: history.units,
    topics: history.topics,
    cards: history.cards,
    events: history.events,
  });
  return {
    ...history,
    cards: history.cards.map((card) => ({
      ...card,
      unlocked: progression.isCardUnlocked(card.id),
    })),
    sections: history.sections.map((section) => ({
      ...section,
      unlocked: progression.isEntityUnlocked(section.id),
    })),
    units: history.units.map((unit) => ({
      ...unit,
      unlocked: progression.isEntityUnlocked(unit.id),
    })),
    topics: history.topics.map((topic) => ({
      ...topic,
      unlocked: progression.isEntityUnlocked(topic.id),
    })),
  };
}

export function scopeDeckHistory(
  history: DeckHistory,
  scope: DeckScope = {},
): DeckHistory | null {
  if (!scope.sectionSlug) return history;
  const section = history.sections.find(
    (candidate) => candidate.slug === scope.sectionSlug,
  );
  if (!section) return null;
  const units = history.units.filter(
    (candidate) => candidate.sectionId === section.id,
  );
  const unit = scope.unitSlug
    ? units.find((candidate) => candidate.slug === scope.unitSlug)
    : undefined;
  if (scope.unitSlug && !unit) return null;
  const scopedUnits = unit ? [unit] : units;
  const unitIds = new Set(scopedUnits.map((candidate) => candidate.id));
  const topics = history.topics.filter((candidate) =>
    unitIds.has(candidate.unitId),
  );
  const topic = scope.topicSlug
    ? topics.find((candidate) => candidate.slug === scope.topicSlug)
    : undefined;
  if (scope.topicSlug && !topic) return null;
  const scopedTopics = topic ? [topic] : topics;
  const topicIds = new Set(scopedTopics.map((candidate) => candidate.id));
  const cards = history.cards.filter((card) => topicIds.has(card.topicId));
  const cardIds = new Set(cards.map((card) => card.id));
  return {
    ...history,
    cards,
    events: history.events.filter((event) => cardIds.has(event.cardId)),
    sections: [section],
    topics: scopedTopics,
    units: scopedUnits,
  };
}

export function deriveDeckHistoryStats(history: DeckHistory) {
  const activeCardIds = new Set(
    history.cards
      .filter((card) => card.active && card.unlocked !== false)
      .map((card) => card.id),
  );
  const touched = new Set<string>();
  const correct = new Set<string>();
  for (const event of history.events) {
    if (!activeCardIds.has(event.cardId)) continue;
    touched.add(event.cardId);
    if (event.result === 'correct') correct.add(event.cardId);
  }
  const lockedCardCount = history.cards.filter(
    (card) => card.active && card.unlocked === false,
  ).length;
  return {
    cardCount: activeCardIds.size,
    everCorrectCount: correct.size,
    touchedCount: touched.size,
    ...(lockedCardCount ? { lockedCardCount } : {}),
  };
}

export async function completeDeckJoin(input: {
  auth: {
    signIn: () => Promise<unknown>;
    signUp: (locale: string) => Promise<unknown>;
  };
  join: (slug: string) => Promise<{ deckId: string }>;
  locale: string;
  navigate: (slug: string) => void;
  refetch: () => Promise<unknown>;
  signUp: boolean;
  signedIn: boolean;
  slug: string;
}) {
  if (!input.signedIn) {
    if (input.signUp) await input.auth.signUp(input.locale);
    else await input.auth.signIn();
    await input.refetch();
  }
  await input.join(input.slug);
  await input.refetch();
  input.navigate(input.slug);
}

export function uploadErrorMessage(error: unknown): UploadMessageId {
  if (error instanceof ApiResponseError && error.status === 413)
    return 'web.deckPage.uploadTooLarge';
  if (error instanceof Error && error.message === 'Unsupported PDF')
    return 'web.deckPage.uploadUnsupported';
  if (error instanceof Error && error.message === 'Invalid PDF')
    return 'web.deckPage.uploadInvalidPdf';
  if (error instanceof Error && error.message === 'PDF page count mismatch')
    return 'web.deckPage.uploadPageMismatch';
  return 'web.deckPage.uploadFailed';
}

export function isUploadFailure(
  messageId: UploadMessageId | undefined,
): boolean {
  switch (messageId) {
    case 'web.deckPage.uploadInvalidPdf':
    case 'web.deckPage.uploadPageMismatch':
    case 'web.deckPage.uploadTooLarge':
    case 'web.deckPage.uploadUnsupported':
    case 'web.deckPage.uploadFailed':
      return true;
    case 'web.deckPage.uploadSuccess':
    case undefined:
      return false;
  }
}
